import { useEffect, useMemo, useRef } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";

function getCircleCount() {
  if (typeof window === "undefined") return 15;
  return window.innerWidth < 768 ? 10 : 15;
}

export function OpArtBackgroundSvg({ scrollProgress }: { scrollProgress: number }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const rafRef = useRef<number>(0);
  const reducedMotion = useReducedMotion();
  const circleCount = useMemo(getCircleCount, []);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const circles = svg.querySelectorAll("circle");
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = (now - startTime) / 1000;

      circles.forEach((circle, i) => {
        const phase = (i / circleCount) * Math.PI * 2;
        const speed = 0.3 + i * 0.02;

        const timeComponent = reducedMotion ? 0 : Math.sin(elapsed * speed + phase) * 15;
        const scrollComponent = scrollProgress * 30;

        const baseRadius = 50 + i * 35;
        const radius = baseRadius + timeComponent + scrollComponent * (i % 2 === 0 ? 1 : -1);

        const cx = 50 + Math.sin(phase + (reducedMotion ? 0 : elapsed * 0.1)) * scrollProgress * 5;
        const cy = 50 + Math.cos(phase + (reducedMotion ? 0 : elapsed * 0.08)) * scrollProgress * 5;

        circle.setAttribute("r", String(Math.max(0, radius)));
        circle.setAttribute("cx", `${cx}%`);
        circle.setAttribute("cy", `${cy}%`);
        circle.setAttribute(
          "stroke-width",
          String(0.5 + scrollProgress * 0.5 + (i % 3 === 0 ? 0.3 : 0))
        );
      });

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [scrollProgress, reducedMotion, circleCount]);

  return (
    <svg
      ref={svgRef}
      className="fixed inset-0 w-full h-full pointer-events-none z-0"
      style={{ willChange: "transform" }}
      aria-hidden="true"
    >
      {Array.from({ length: circleCount }, (_, i) => (
        <circle
          key={i}
          cx="50%"
          cy="50%"
          r={50 + i * 35}
          fill="none"
          stroke="var(--color-circle-stroke)"
          strokeWidth="0.5"
        />
      ))}
    </svg>
  );
}
